<script context="module" lang="ts">
  export type PriceHistory = {
    price: number;
    date: Date;
    formattedUnitAmount: string;
  };

  export type PriceHistoryTooltip = {
    title: string;
    body: string;
    x: number;
    y: number;
  };
</script>

<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import { writable } from "svelte/store";
  import { format } from "date-fns";
  import { line, area, curveMonotoneX } from "d3-shape";
  import { scaleLinear, scaleTime } from "d3-scale";
  import { transition as d3Transition } from "d3-transition";
  import { type Selection, select as d3Select } from "d3-selection";
  import BasicTooltip from "../tooltips/BasicTooltip.svelte";
  d3Select.prototype.transition = d3Transition; // bug in d3 types

  const TOOLTIP_CUTOFF = 120;
  const RIGHT_OFFSET = 80;

  export let values: Array<PriceHistory> = [];
  export let lineColor: string;

  let container: HTMLElement;
  let svgSelection: Selection<SVGGElement, any, null, any>;
  let svg: SVGGElement;
  let margin = { top: 16, right: 16, bottom: 8, left: 16 };
  let width: number;
  let height: number;
  const tooltip = writable<PriceHistoryTooltip | undefined>(undefined);

  const calculateDimensions = () => {
    if (container) {
      const rect = container.getBoundingClientRect();
      width = rect.width - margin.left - margin.right;
      height = rect.height - margin.top - margin.bottom;
    }
  };

  function onMouseMove(this: SVGRectElement, event: any): void {
    const x =
      event.clientX > window.innerWidth - TOOLTIP_CUTOFF
        ? event.layerX - RIGHT_OFFSET
        : event.layerX;
    tooltip.update((t) => {
      if (t) {
        return { ...t, x: x, y: event.layerY };
      }
      return t;
    });
  }

  function onMouseOver(this: SVGRectElement, event: any, i: PriceHistory): void {
    // Get matching circle for overlay area
    const index = values.findIndex((d) => {
      return d.date.getTime() === i.date.getTime();
    });
    if (index === -1) return;

    const correspondingCircle = svgSelection.select(`#dot-${index}`);

    correspondingCircle
      .transition()
      .duration(100)
      .attr("r", 6)
      .style("fill", "#ffffff")
      .attr("stroke", lineColor)
      .attr("stroke-width", 2);

    // Tooltip logic
    const x =
      event.clientX > window.innerWidth - TOOLTIP_CUTOFF
        ? event.layerX - RIGHT_OFFSET
        : event.layerX;

    tooltip.set({
      title: format(i.date, "dd MMM"),
      body: i.formattedUnitAmount,
      x: x,
      y: event.layerY,
    });
  }

  function onMouseOut(this: SVGRectElement, event: any, i: PriceHistory): void {
    // Get matching circle for overlay area
    const index = values.findIndex((d) => {
      return d.date.getTime() === i.date.getTime();
    });
    const correspondingCircle = svgSelection.select(`#dot-${index}`);

    correspondingCircle
      .transition()
      .duration(100)
      .attr("r", 4)
      .style("fill", lineColor)
      .attr("stroke", "#ffffff");

    // Clear the tooltip
    tooltip.set(undefined);
  }

  onDestroy(() => {
    tooltip.set(undefined);
    svgSelection.remove();
  });

  onMount(() => {
    calculateDimensions();

    const prices = values.map((d) => d.price);

    const x = scaleTime()
      .domain([values[0].date, values[values.length - 1].date])
      .range([margin.left, width]);

    const y = scaleLinear()
      .domain([Math.min(...prices), Math.max(...prices)])
      .range([height - margin.bottom, margin.top]);

    const lineGenerator = line<PriceHistory>()
      .x((d: PriceHistory) => x(d.date))
      .y((d: PriceHistory) => y(d.price))
      .curve(curveMonotoneX);

    var bottomArea = area<PriceHistory>()
      .x(function (d) {
        return x(d.date);
      })
      .y0(height)
      .y1(function (d) {
        return y(d.price);
      });

    svgSelection = d3Select(svg).append("g");

    svgSelection
      .append("linearGradient")
      .attr("id", `area-gradient-${lineColor}`)
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: `${lineColor}33` },
        { offset: "100%", color: `${lineColor}00` },
      ])
      .enter()
      .append("stop")
      .attr("offset", function (d) {
        return d.offset;
      })
      .attr("stop-color", function (d) {
        return d.color;
      });

    svgSelection
      .append("path")
      .datum(values)
      .style("fill", `url(#area-gradient-${lineColor})`)
      .attr("d", bottomArea);

    svgSelection
      .append("path")
      .datum(values)
      .attr("fill", "none")
      .attr("stroke", lineColor)
      .attr("stroke-width", 2)
      .attr("d", lineGenerator);

    // Create overlays that occupy half the distance to next point
    svgSelection
      .selectAll(".overlay")
      .data(values)
      .enter()
      .append("rect")
      .attr("class", "overlay")
      .attr("id", (d, i) => `overlay-${i}`)
      .attr("x", (d, i) => {
        if (i === 0) {
          return x(d.date);
        } else {
          // Use the previous overlay's end position as x
          return (x(values[i - 1].date) + x(d.date)) / 2;
        }
      })
      .attr("y", 0)
      .attr("width", (d, i) => {
        // Calculate half the distance to the next point
        if (i < values.length - 1) {
          const next = x(values[i + 1].date);
          const current = i === 0 ? x(d.date) : (x(values[i - 1].date) + x(d.date)) / 2;
          return next - current;
        } else {
          // Default width for last point
          return 100;
        }
      })
      .attr("height", 64)
      .style("pointer-events", "all")
      .style("fill", "transparent")
      .on("mouseover", onMouseOver)
      .on("mouseout", onMouseOut)
      .on("mousemove", onMouseMove);

    svgSelection
      .selectAll(".dot")
      .data(values)
      .enter()
      .append("circle")
      .attr("class", "dot")
      .attr("id", (d, i) => `dot-${i}`)
      .attr("cx", (d) => x(d.date))
      .attr("cy", (d: PriceHistory) => y(d.price))
      .attr("r", 4)
      .style("fill", lineColor)
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 2)
      .style("pointer-events", "none");
  });
</script>

<div class="container" bind:this={container}>
  <svg id="chart-svg" bind:this={svg} />

  {#if $tooltip}
    <BasicTooltip
      x={$tooltip.x}
      y={$tooltip.y}
      offset={{ x: 8, y: 8 }}
      title={$tooltip.title}
      subtitle={$tooltip.body}
      textColor={"var(--rk-text-transparency-light)"}
      subtitleColor={"var(--rk-text-light)"}
      textSize={"xs"}
    />
  {/if}
</div>

<style>
  .container {
    height: 64px;
    padding: 8px;
  }

  svg {
    width: 100%;
    height: 100%;
  }
</style>
